
import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { AuthLoadingState } from '@/components/auth/AuthLoadingState';
import { AppRole } from '@/types/auth';
import { securityService } from '@/services/securityService';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
  fallbackPath?: string;
}

export const ProtectedRoute = ({ 
  children, 
  requiredRoles = [], 
  fallbackPath = '/auth' 
}: ProtectedRouteProps) => {
  const { user, loading, loadingMessage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [roleCheckLoading, setRoleCheckLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);

  useEffect(() => {
    // Prevent running if already navigated
    if (hasNavigated) return;

    const checkAccess = async () => {
      if (!loading) {
        if (!user) {
          setHasNavigated(true);
          // User not authenticated, redirect to auth with current path
          const redirectUrl = `${fallbackPath}?redirect=${encodeURIComponent(location.pathname + location.search)}`;
          navigate(redirectUrl, { replace: true });
          return;
        }

        if (requiredRoles.length > 0) {
          // Validate roles using secure service
          const hasRequiredRole = await securityService.validateUserHasAnyRole(requiredRoles);
          
          if (!hasRequiredRole) {
            // Log security event for unauthorized access attempt
            await securityService.logSecurityEvent('unauthorized_access_attempt', {
              user_id: user.id,
              attempted_path: location.pathname,
              required_roles: requiredRoles,
              user_roles: user.roles
            });
            
            setHasNavigated(true);
            // User doesn't have required role, redirect to home
            navigate('/', { replace: true });
            return;
          }
        }

        setHasAccess(true);
      }
      setRoleCheckLoading(false);
    };

    checkAccess();
  }, [user, loading, requiredRoles.join(','), fallbackPath, location.pathname, hasNavigated, navigate]);

  if (loading || roleCheckLoading) {
    return <AuthLoadingState message={loadingMessage || 'Verifying access permissions...'} />;
  }

  if (!user || !hasAccess) {
    return null; // Will redirect via useEffect
  }

  return <>{children}</>;
};
